import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronDown } from 'lucide-react';
import axios from 'axios';
import WeatherWidget from '@/components/WeatherWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CLD_DEFAULT = 'https://res.cloudinary.com/amenitiz/image/upload/q_auto,f_auto,w_1600/v1695021840/jxscs29isbyunx4jx3e2.jpg';

function getContextualGreeting() {
  const h = new Date().getHours();
  if (h >= 6 && h < 12) return 'Bonjour';
  if (h >= 12 && h < 18) return 'Bel apres-midi';
  if (h >= 18 && h < 23) return 'Bonsoir';
  return 'Bonne nuit';
}

const easeLuxe = [0.16, 1, 0.3, 1];

export default function Hero() {
  const [hero, setHero] = useState(null);
  const [embedFailed, setEmbedFailed] = useState(false);
  const [greeting] = useState(getContextualGreeting);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/media/hero`).then(r => setHero(r.data)).catch(() => {});
  }, []);

  const scrollToSection = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const heroType = hero?.type || 'default';
  const media = hero?.media;

  const renderBackground = () => {
    if (heroType === 'image' && media?.files) {
      const src = `${BACKEND_URL}/api/media/file/${media.files.desktop || media.files.mobile}`;
      return <img src={src} alt={media.title || 'Le Domaine du Pilat'} className="w-full h-full object-cover" loading="eager" />;
    }
    if (heroType === 'video' && media?.files?.original) {
      return (
        <video
          src={`${BACKEND_URL}/api/media/file/${media.files.original}`}
          className="w-full h-full object-cover"
          autoPlay muted loop playsInline
        />
      );
    }
    if (heroType === 'embed' && hero?.embed_url && !embedFailed) {
      // YouTube embed - we render but also pre-load the default image as a fallback layer behind
      const ytId = (hero.embed_url.match(/[a-zA-Z0-9_-]{11}/) || [])[0];
      return (
        <>
          <img src={CLD_DEFAULT} alt="Le Domaine du Pilat" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
          {ytId && (
            <iframe
              src={`${hero.embed_url}?autoplay=1&mute=1&loop=1&controls=0&playlist=${ytId}&modestbranding=1&showinfo=0&rel=0`}
              className="relative w-full h-full"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              title="Hero video"
              onError={() => setEmbedFailed(true)}
              style={{ border: 'none' }}
            />
          )}
        </>
      );
    }
    return <img src={CLD_DEFAULT} alt="Le Domaine du Pilat - Vue exterieure" className="w-full h-full object-cover" loading="eager" />;
  };

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative w-full h-screen min-h-[640px] overflow-hidden bg-[var(--bg-primary)]"
    >
      {/* Framed media artwork - full bleed with subtle inset on desktop */}
      <div className="absolute inset-0 md:inset-3 lg:inset-5 md:rounded-[2rem] overflow-hidden">
        {renderBackground()}
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content overlay */}
      <div className="relative z-10 h-full flex flex-col justify-end px-8 md:px-16 lg:px-24 pb-20 md:pb-24 lg:pb-28">
        <motion.div
          initial="hidden"
          animate="show"
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.12, delayChildren: 0.4 } }
          }}
          className="max-w-3xl"
        >
          <motion.span
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.9, ease: easeLuxe }}
            data-testid="hero-greeting"
            className="block text-[#CBA77A] text-xs md:text-sm uppercase tracking-[0.3em] font-medium mb-6"
          >
            {greeting} &middot; Parc du Pilat
          </motion.span>

          <motion.h1
            variants={{ hidden: { opacity: 0, y: 30 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 1.1, ease: easeLuxe }}
            data-testid="hero-title"
            className="font-['Cormorant_Garamond'] text-[3.25rem] sm:text-7xl md:text-8xl lg:text-[9rem] text-white font-light tracking-[-0.025em] leading-[0.95]"
          >
            Le Domaine
            <span className="block italic text-white/95">du Pilat</span>
          </motion.h1>

          <motion.p
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.9, ease: easeLuxe }}
            className="mt-8 md:mt-10 text-white/75 text-base md:text-lg max-w-xl leading-relaxed font-light tracking-wide"
          >
            Gite de charme &amp; salle de reception au coeur de la nature.
            <span className="block mt-2 text-white/55 text-sm tracking-wider">
              20 couchages &middot; Salle 150 personnes &middot; Parking prive
            </span>
          </motion.p>

          <motion.div
            variants={{ hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0 } }}
            transition={{ duration: 0.9, ease: easeLuxe }}
            className="flex flex-col sm:flex-row gap-3 mt-12"
          >
            <Button
              data-testid="hero-reserve-btn"
              onClick={() => scrollToSection('#reservation')}
              className="group btn-arrow-hover rounded-full px-9 py-6 text-[11px] bg-[#CBA77A] hover:bg-[#B89665] text-[#1A1C19] font-semibold tracking-[0.22em] uppercase shadow-[0_8px_30px_rgba(203,167,122,0.35)] transition-all"
              size="lg"
            >
              <span>Reserver</span>
              <ArrowRight className="arrow-icon w-4 h-4 ml-3" strokeWidth={2} />
            </Button>
            <Button
              data-testid="hero-discover-btn"
              onClick={() => scrollToSection('#hebergements')}
              variant="ghost"
              className="rounded-full px-9 py-6 text-[11px] text-white hover:bg-white/10 font-medium tracking-[0.22em] uppercase border border-white/25 transition-all"
              size="lg"
            >
              Decouvrir le domaine
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Weather widget pill - bottom right */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.5, ease: easeLuxe }}
        className="absolute bottom-6 right-6 md:bottom-10 md:right-12 z-10"
      >
        <WeatherWidget />
      </motion.div>

      {/* Scroll indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 2 }}
        onClick={() => scrollToSection('#hebergements')}
        aria-label="Defiler vers le bas"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-white/45 hover:text-white/85 transition-colors group"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Decouvrir</span>
        <ChevronDown className="w-5 h-5 animate-bounce" strokeWidth={1.5} />
      </motion.button>
    </section>
  );
}
